import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/theme-context';

const PageNotFound: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      <div className="max-w-2xl w-full text-center">
        {/* Animated 404 */}
        <div className="relative mb-8">
          <h1 className={`text-[150px] sm:text-[200px] font-bold leading-none transition-colors duration-300 ${
            theme === 'dark' 
              ? 'text-gray-800' 
              : 'text-gray-200'
          }`}>
            404
          </h1>
          <div className="absolute inset-0 flex items-center justify-center">
            <AlertCircle 
              className={`w-16 h-16 sm:w-24 sm:h-24 animate-pulse transition-colors duration-300 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-500'
              }`} 
            />
          </div>
        </div>

        {/* Title and Description */}
        <div className="space-y-4 mb-8">
          <h2 className={`text-3xl sm:text-4xl font-bold transition-colors duration-300 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Oops! Page Not Found
          </h2>
          <p className={`text-lg sm:text-xl transition-colors duration-300 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            The page you're looking for doesn't exist or has been moved.
          </p>
        </div>

        {/* Decorative Elements */}
        <div className="flex justify-center gap-4 mb-12">
          <div className={`w-2 h-2 rounded-full animate-bounce transition-colors duration-300 ${
            theme === 'dark' ? 'bg-blue-400' : 'bg-blue-500'
          }`} style={{ animationDelay: '0ms' }}></div>
          <div className={`w-2 h-2 rounded-full animate-bounce transition-colors duration-300 ${
            theme === 'dark' ? 'bg-purple-400' : 'bg-purple-500'
          }`} style={{ animationDelay: '150ms' }}></div>
          <div className={`w-2 h-2 rounded-full animate-bounce transition-colors duration-300 ${
            theme === 'dark' ? 'bg-pink-400' : 'bg-pink-500'
          }`} style={{ animationDelay: '300ms' }}></div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate(-1)}
            className={`group flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
              theme === 'dark'
                ? 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-700'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 shadow-sm hover:shadow-md'
            }`}
          >
            <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            Go Back
          </button>
          
          <button
            onClick={() => navigate('/')}
            className={`group flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
              theme === 'dark'
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-blue-500/50'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
            }`}
          >
            <Home className="w-5 h-5 transition-transform group-hover:scale-110" />
            Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageNotFound;